import SummaryFrame from "../../common/components/ConfigurationSummaryComponent/SummaryFrame/SummaryFrame";
import EditButton from "../../common/components/ConfigurationSummaryComponent/EditButton/EditButton";
import { useSelector } from "react-redux";
import { type RootState } from "../../../redux/store";
import {
  getHighestVisitedStep,
  getIsCustomSegmentLengthSelected,
  getKmats,
  getRailKmatCsticMap,
} from "../../Linear/redux/selectors";
import DataDrivenTable from "../../../common/components/Table/DataDrivenTable";
import {
  CSTIC_AL_NAME,
  CSTIC_AR_NAME,
  CSTIC_GUIDEWAY_LOCATING_FACE,
  CSTIC_LENGTH_NAME,
  CSTIC_PART_AMOUNT_NAME,
  CSTIC_PART_SIZING_TYPE_NAME,
  GET_GUIDEWAY_PIECE_SIZE_CSTIC_NAME,
} from "../../Linear/constants";
import { getCsticNumberVal } from "../../Linear/utils";
import { NumberFormatter as NF } from "../../../utils/NumberFormatter";
import {
  generateClosureSubstepData,
  generateSimpleSubstepData,
  prepareTableView,
} from "../../Linear/components/LinearSummaryData";
import { useTranslation, type WithTranslation } from "react-i18next";
import { getConfiguratorDefinition } from "~/Configurators/configuratorRegistry";
import { formatCsticValueAsNumber } from "~/Configurators/Shaft/components/useMinMaxCsticLabel";
import { useXmlConfiguratorTranslations } from "~/Configurators/common/useXmlConfiguratorTranslations";
import { RAIL_CONFIGURATOR_PRODUCT_CODE } from "~/Configurators/constants";

const TWO_COL_TABLE_FULL_PAGE = ["30%", "70%"];
const TWO_COL_TABLE_REVIEW = ["50%", "50%"];

const getSubsteps = (state: RootState) => state.linearConfigurator.substeps;

interface Props {
  isFullReview: boolean;
  onStepChange?: () => void;
}

export const RailSummaryData = ({ isFullReview, onStepChange }: Props) => {
  const { t } = useTranslation();
  const xmlT = useXmlConfiguratorTranslations().t!;
  const dataTableHeader = [t("linearConfigSummary.step.header")];
  const twoColTable = isFullReview ? TWO_COL_TABLE_FULL_PAGE : TWO_COL_TABLE_REVIEW;

  const step1Data = useSelector(prepareStep1Data);
  const step2Data = useSelector(prepareStep2Data);
  const step3Data = useSelector(prepareStep3Data(xmlT));
  const step4Data = useSelector(prepareStep4Data(t));

  const step1Name = xmlT(useSelector(selectStepName(0)));
  const step2Name = xmlT(useSelector(selectStepName(1)));
  const step3Name = xmlT(useSelector(selectStepName(2)));
  const step4Name = xmlT(useSelector(selectStepName(3)));

  return (
    <>
      {step1Data && (
        <SummaryFrame num={1} name={step1Name}>
          <div>
            <h3 className="pb-0">
              <div>{step1Data.baseDesignation}</div>
            </h3>
            <EditButton index={0} onStepChange={onStepChange} />
          </div>
        </SummaryFrame>
      )}
      {step2Data && (
        <SummaryFrame num={2} name={step2Name}>
          <DataDrivenTable header={dataTableHeader} data={step2Data} columnFlex={twoColTable} />
          <EditButton index={1} onStepChange={onStepChange} />
        </SummaryFrame>
      )}
      {step3Data && (
        <SummaryFrame num={3} name={step3Name}>
          <DataDrivenTable header={dataTableHeader} data={step3Data} columnFlex={twoColTable} />
          <EditButton index={2} onStepChange={onStepChange} />
        </SummaryFrame>
      )}
      {step4Data && (
        <SummaryFrame num={4} name={step4Name}>
          <DataDrivenTable header={dataTableHeader} data={step4Data} columnFlex={twoColTable} />
          <EditButton index={3} onStepChange={onStepChange} />
        </SummaryFrame>
      )}
    </>
  );
};

const prepareStep1Data = (state: RootState) => {
  return {
    baseDesignation: getConfiguratorDefinition(RAIL_CONFIGURATOR_PRODUCT_CODE).generateDesignation!(
      state.linearConfigurator.kmats,
    ),
  };
};

const selectStepName = (index: number) => (state: RootState) => {
  return state.linearConfigurator.steps[index]?.name ?? "";
};

const prepareStep2Data = (state: RootState) => {
  const highestVisitedStep = getHighestVisitedStep(state);
  if (highestVisitedStep < 1) {
    // 2 - 1, because 0 indexed
    return null;
  }

  const substeps = getSubsteps(state);
  const kmats = getKmats(state);

  const step2Data = [];

  const seriesData = generateSimpleSubstepData(substeps.Coating, kmats);
  if (seriesData) {
    step2Data.push(seriesData);
  }
  const sizeData = generateSimpleSubstepData(substeps.Accuracy, kmats);
  if (sizeData) {
    step2Data.push(sizeData);
  }

  if (step2Data.length > 0) {
    return prepareTableView(step2Data);
  } else {
    return null;
  }
};

const prepareStep3Data = (t: WithTranslation["t"]) => (state: RootState) => {
  const highestVisitedStep = getHighestVisitedStep(state);
  if (highestVisitedStep < 2) {
    // 3 - 1, because 0 indexed
    return null;
  }

  const substeps = getSubsteps(state);
  const kmats = getKmats(state);

  const step3Data = [];

  const mountingData = generateSimpleSubstepData(substeps.Mounting, kmats);
  if (mountingData) {
    step3Data.push(mountingData);
  }
  if (substeps.Closure.isVisible) {
    const closureData = generateClosureSubstepData(substeps.Closure, kmats, t);
    if (closureData) {
      step3Data.push(closureData);
    }
  }

  if (step3Data.length > 0) {
    return prepareTableView(step3Data);
  } else {
    return null;
  }
};
const prepareStep4Data = (t: WithTranslation["t"]) => (state: RootState) => {
  const highestVisitedStep = getHighestVisitedStep(state);
  const customSegmentLengthSelected = getIsCustomSegmentLengthSelected(0)(state);
  if (highestVisitedStep < 2) {
    // 3 - 1, because 0 indexed
    return null;
  }

  const substeps = getSubsteps(state);
  const railKmat = getRailKmatCsticMap(state);

  const step4data = [];

  const locatingFaceCstic = railKmat[CSTIC_GUIDEWAY_LOCATING_FACE];
  if (locatingFaceCstic && locatingFaceCstic.value !== null) {
    const val = locatingFaceCstic.values?.find((v) => v.value === locatingFaceCstic.value);
    const isTop = locatingFaceCstic.value === "O";
    step4data.push({
      name: substeps.LocatingFace.name,
      value: val ? t(val.label) : "-",
      isStandard: isTop,
    });
  }

  const railLengthValue = railKmat[CSTIC_LENGTH_NAME].value != null ? railKmat[CSTIC_LENGTH_NAME].value : "-";
  step4data.push({
    name: t("linearConfigSummary.step6.1lane"),
    // name: railKmat[CSTIC_LENGTH_NAME].label,
    value: formatCsticValueAsNumber(railLengthValue),
    isStandard: false,
  });

  if (railKmat[CSTIC_AL_NAME] && railKmat[CSTIC_AL_NAME]?.value !== null) {
    const alValue = getCsticNumberVal(railKmat[CSTIC_AL_NAME]);
    step4data.push({
      name: t("linearConfigSummary.step6.2lane"),
      // name: railKmat[CSTIC_AL_NAME].label,
      value:
        alValue !== null ? NF.getDefaultFormatter().format(Math.round((alValue + Number.EPSILON) * 100) / 100) : "",
      isStandard: false,
    });
  }
  if (railKmat[CSTIC_AR_NAME] && railKmat[CSTIC_AR_NAME].value !== null) {
    const arValue = getCsticNumberVal(railKmat[CSTIC_AR_NAME]);
    step4data.push({
      name: t("linearConfigSummary.step6.3lane"),
      // name: railKmat[CSTIC_AR_NAME].label,
      value:
        arValue !== null ? NF.getDefaultFormatter().format(Math.round((arValue + Number.EPSILON) * 100) / 100) : "",
      isStandard: false,
    });
  }

  if (
    customSegmentLengthSelected &&
    railKmat[CSTIC_PART_SIZING_TYPE_NAME] &&
    railKmat[CSTIC_PART_SIZING_TYPE_NAME].value !== null
  ) {
    step4data.push({
      name: t("linearConfigSummary.step6.4lane"),
      // name: railKmat[CSTIC_PART_SIZING_TYPE_NAME].label,
      value:
        railKmat[CSTIC_PART_SIZING_TYPE_NAME].values.find(
          (v) => v.value === railKmat[CSTIC_PART_SIZING_TYPE_NAME].value,
        )?.label ?? "-",
      isStandard: false,
    });
  }

  //PARTS
  // const partsValue = railKmat[CSTIC_PART_AMOUNT_NAME]?.values.find((val) => val.value === railKmat[CSTIC_PART_AMOUNT_NAME].value);
  const partsValue =
    railKmat[CSTIC_PART_AMOUNT_NAME].value != null
      ? t("linearConfigSummary.step6.Partsvalue", { count: Number(railKmat[CSTIC_PART_AMOUNT_NAME].value) })
      : "-";
  step4data.push({
    // name: railKmat[CSTIC_PART_AMOUNT_NAME].label,
    name: t("linearConfigSummary.step6.5lane"),
    // value: partsValue?.label ?? "-",
    value: partsValue,
    isStandard: false,
  });

  for (let j = 1; j <= 5; j++) {
    const cstic = railKmat[GET_GUIDEWAY_PIECE_SIZE_CSTIC_NAME(j)];
    if (cstic) {
      const csticNumericValue = getCsticNumberVal(cstic);
      step4data.push({
        name: t("linearConfigSummary.steps6.partialLength", { count: j }),
        value: csticNumericValue !== null ? NF.getDefaultFormatter().format(csticNumericValue) : "-",
        isStandard: false,
      });
    }
  }

  if (step4data.length > 0) {
    return prepareTableView(step4data);
  } else {
    return null;
  }
};
